'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var errors = require('../errors.cjs');
var Bid = require('../models/Bid.cjs');
var Operation = require('../../../types/Operation.cjs');
var PublicKey = require('../../../types/PublicKey.cjs');
var DateTime = require('../../../types/DateTime.cjs');
var TransactionBuilder = require('../../../utils/TransactionBuilder.cjs');

// -----------------
// Operation
// -----------------

const Key = 'DirectSellOperation';

/**
 * Creates a listing on a given asset and then executes a sell on the created bid and listing.
 *
 * ```ts
 * await metaplex
 *   .auctionHouse()
 *   .sell({ auctionHouse, bid };
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const directSellOperation = Operation.useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const directSellOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    const builder = await directSellBuilder(metaplex, operation.input, scope);
    scope.throwIfCanceled();
    return builder.sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Creates a listing on a given asset and executes a sale on the created listing and given bid.
 *
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .auctionHouse()
 *   .builders()
 *   .sell({ auctionHouse, bid, seller })
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const directSellBuilder = async (metaplex, params, options = {}) => {
  // Data.
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    auctionHouse,
    auctioneerAuthority,
    bid,
    seller = metaplex.identity(),
    authority = auctionHouse.authorityAddress,
    bookkeeper = metaplex.identity(),
    createListingInstructionKey,
    executeSaleInstructionKey
  } = params;
  const {
    hasAuctioneer
  } = auctionHouse;
  const {
    tokens,
    price,
    buyerAddress
  } = bid;
  const printReceipt = (params.printReceipt ?? true) && Boolean(bid.receiptAddress);
  if (hasAuctioneer && !auctioneerAuthority) {
    throw new errors.AuctioneerAuthorityRequiredError();
  }
  const asset = Bid.isPrivateBid(bid) ? bid.asset : {
    ...bid.asset,
    token: params.sellerToken
  };
  const listingBuilder = metaplex.auctionHouse().builders().list({
    mintAccount: asset.mint.address,
    price,
    auctionHouse,
    auctioneerAuthority,
    seller,
    authority,
    tokenAccount: asset.token.address,
    tokens,
    printReceipt,
    bookkeeper,
    instructionKey: createListingInstructionKey
  }, {
    programs,
    payer
  });
  const {
    receipt,
    sellerTradeState
  } = listingBuilder.getContext();
  const listing = {
    model: 'listing',
    lazy: false,
    auctionHouse,
    asset,
    tradeStateAddress: sellerTradeState,
    bookkeeperAddress: PublicKey.toPublicKey(bookkeeper),
    sellerAddress: PublicKey.toPublicKey(seller),
    receiptAddress: receipt,
    purchaseReceiptAddress: null,
    price,
    tokens,
    createdAt: DateTime.now(),
    canceledAt: null
  };
  const saleBuilder = metaplex.auctionHouse().builders().executeSale({
    auctionHouse,
    auctioneerAuthority,
    bid,
    listing,
    printReceipt,
    bookkeeper,
    instructionKey: executeSaleInstructionKey
  }, {
    programs,
    payer
  });
  const {
    receipt: receiptAddress
  } = saleBuilder.getContext();
  const buyerTokenAccount = metaplex.tokens().pdas().associatedTokenAccount({
    mint: asset.address,
    owner: buyerAddress,
    programs
  });
  const purchasedAsset = {
    ...asset,
    token: {
      ...asset.token,
      address: buyerTokenAccount,
      ownerAddress: buyerAddress
    }
  };
  const purchase = {
    auctionHouse,
    model: 'purchase',
    lazy: false,
    asset: purchasedAsset,
    buyerAddress,
    sellerAddress: PublicKey.toPublicKey(seller),
    bookkeeperAddress: PublicKey.toPublicKey(bookkeeper),
    receiptAddress,
    price: bid.price,
    tokens,
    createdAt: DateTime.now()
  };
  return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer).setContext({
    listing,
    purchase
  }).add(listingBuilder).add(saleBuilder);
};

exports.directSellBuilder = directSellBuilder;
exports.directSellOperation = directSellOperation;
exports.directSellOperationHandler = directSellOperationHandler;
//# sourceMappingURL=directSell.cjs.map
